'use client'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    useSyncExternalStore,
    type ReactNode,
} from 'react'

import { useDatadog, useLogger, useWapi } from '@/app/hooks'
import { type SessionContext } from '@/services/auth'
import { performLogout } from '@/services/auth/client/logout'
import {
    clearMirror,
    getServerSessionSnapshot,
    getSessionSnapshot,
    readMirrorToken,
    subscribeToMirror,
} from '@/services/auth/client/session-mirror'
import { setSnowplowUserId } from '@/services/snowplow/set-user-id'

interface SessionContextValue {
    session: SessionContext
    /** Log out in place (client-direct): WAPI logout + clear mirror + clear analytics identity. */
    logout: () => Promise<void>
}

const SessionStateContext = createContext<SessionContextValue | null>(null)

/**
 * Provides client session state to the React tree. The session is read from the
 * localStorage mirror via `useSyncExternalStore`: the **server snapshot is always
 * logged-out** (so SSR/SSG HTML stays session-blind and cached pages remain
 * byte-identical), and the client snapshot resolves the real state after
 * hydration. Cross-tab mirror changes update it live. Sits beside `ClientEnvProvider`.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
    const wapi = useWapi()
    const datadog = useDatadog()
    const logger = useLogger({ module: 'session-provider' })

    const mirrorSession = useSyncExternalStore(
        subscribeToMirror,
        getSessionSnapshot,
        getServerSessionSnapshot,
    )

    // The name is not in the mirror — fetch it from userinfo.json post-hydration so the
    // navbar can greet by name (FR-15). Best-effort: a failed fetch leaves no name, and the
    // navbar falls back to the generic logged-in label (never logged-out).
    const [profile, setProfile] = useState<{ firstName?: string; lastName?: string }>({})

    useEffect(() => {
        if (!mirrorSession.isAuthenticated) {
            return
        }
        const token = readMirrorToken()
        if (!token) {
            return
        }
        let cancelled = false
        wapi.getUserInfo(token)
            .then((info) => {
                if (!cancelled) {
                    setProfile({ firstName: info.firstName, lastName: info.lastName })
                }
            })
            .catch((err: unknown) => {
                // best-effort: greeting falls back to the generic label, but log so a
                // systematic userinfo failure is visible (low-cardinality metadata only).
                logger
                    .withMetadata({ error: err instanceof Error ? err.name : 'unknown' })
                    .debug('auth: userinfo greeting fetch failed')
            })
        return () => {
            cancelled = true
        }
    }, [mirrorSession.isAuthenticated, wapi, logger])

    // Surface the fetched name only while authenticated — a stale profile from a previous
    // session is ignored when logged out (and overwritten on the next login's fetch).
    const session: SessionContext = useMemo(
        () =>
            mirrorSession.isAuthenticated
                ? {
                      ...mirrorSession,
                      ...(profile.firstName ? { firstName: profile.firstName } : {}),
                      ...(profile.lastName ? { lastName: profile.lastName } : {}),
                  }
                : mirrorSession,
        [mirrorSession, profile.firstName, profile.lastName],
    )

    const logout = useCallback(async () => {
        await performLogout({
            token: readMirrorToken(),
            logoutRequest: (token) => wapi.logout(token),
            clearMirror,
            setSnowplowUserId,
            clearRumUser: () => {
                datadog.clearUser()
            },
        })
    }, [wapi, datadog])

    return (
        <SessionStateContext.Provider value={{ session, logout }}>
            {children}
        </SessionStateContext.Provider>
    )
}

export function useSession(): SessionContextValue {
    const context = useContext(SessionStateContext)
    if (!context) {
        throw new Error('useSession must be used within a SessionProvider')
    }
    return context
}
